import { hasActiveCategory } from './monthlyNeedCalculations.js';

// Pompe simple (immergée ou de surface) : pas de bascule automatique ni de disconnexion,
// suffisant pour un usage extérieur (arrosage, lavage) sans réseau intérieur sous pression.
export const POMPE_SIMPLE_COST = { low: 150, high: 400 };
export const POMPE_SIMPLE_ELEC_OPEX = { low: 5, high: 15 };

// Barème surpression par tranche de débit (€ HT) : matériel + pose, dimensionné au débit requis.
export const SURPRESSION_TABLE = {
  d2: { label: "Jusqu'à 2 m³/h", mat: [400, 1500], pose: [300, 900] },
  d5: { label: '2,1 à 5 m³/h', mat: [1200, 3500], pose: [600, 1500] },
  d10: { label: '5,1 à 10 m³/h', mat: [3000, 6500], pose: [1200, 2800] },
  dplus: { label: 'Plus de 10 m³/h', mat: [12000, 30000], pose: [2500, 6000] },
};

// Bascule automatique vers le réseau d'eau de ville (appoint + disconnexion normalisée) : remplit
// la cuve à l'eau de ville en cas de manque, pour assurer la continuité des usages critiques.
export const BASCULE_EAU_VILLE_COST = { low: 1200, high: 1700 };
export const BASCULE_EAU_VILLE_OPEX = { low: 5, high: 10 };

// Coût annuel indicatif de l'électricité de pompage (OPEX), par tranche de débit — proportionnel
// au débit installé, pas au volume réellement pompé (simplification volontaire pour ce module).
export const SURPRESSION_ELEC_OPEX = {
  d2: { low: 10, high: 30 },
  d5: { low: 20, high: 50 },
  d10: { low: 35, high: 80 },
  dplus: { low: 60, high: 150 },
};

/**
 * Système de surpression (+ bascule eau de ville) recommandé (non bloquant) dès qu'un usage
 * intérieur sous pression est actif (WC ou lave-linge) — sinon une pompe simple suffit.
 */
export const shouldRecommendSurpression = (usages) =>
  hasActiveCategory(usages, 'wc') || hasActiveCategory(usages, 'washer');

/**
 * @param {Object} pompageState - { mode: 'simple'|'surpression', surpressionDebit, bascule }
 */
export const computePompageCost = (pompageState) => {
  if (pompageState.mode === 'simple') {
    return {
      breakdown: [{ name: 'Pompe simple', low: POMPE_SIMPLE_COST.low, high: POMPE_SIMPLE_COST.high }],
      low: POMPE_SIMPLE_COST.low,
      high: POMPE_SIMPLE_COST.high,
    };
  }

  const tier = SURPRESSION_TABLE[pompageState.surpressionDebit];
  const breakdown = [
    { name: `Surpression — matériel (${tier.label})`, low: tier.mat[0], high: tier.mat[1] },
    { name: 'Pose en local technique', low: tier.pose[0], high: tier.pose[1] },
  ];
  let low = tier.mat[0] + tier.pose[0];
  let high = tier.mat[1] + tier.pose[1];

  if (pompageState.bascule) {
    breakdown.push({ name: 'Bascule automatique eau de ville', low: BASCULE_EAU_VILLE_COST.low, high: BASCULE_EAU_VILLE_COST.high });
    low += BASCULE_EAU_VILLE_COST.low;
    high += BASCULE_EAU_VILLE_COST.high;
  }

  return { breakdown, low, high };
};

/** OPEX annuel (électricité) du pompage, selon le mode + bascule éventuelle. */
export const computePompageOpex = (pompageState) => {
  if (pompageState.mode === 'simple') return POMPE_SIMPLE_ELEC_OPEX;
  const base = SURPRESSION_ELEC_OPEX[pompageState.surpressionDebit] || { low: 0, high: 0 };
  if (!pompageState.bascule) return base;
  return { low: base.low + BASCULE_EAU_VILLE_OPEX.low, high: base.high + BASCULE_EAU_VILLE_OPEX.high };
};

/** Texte court pour l'en-tête replié de la section Pompage. */
export const summarizePompage = (pompageState) => {
  if (pompageState.mode === 'simple') return 'Pompe simple';
  const tier = SURPRESSION_TABLE[pompageState.surpressionDebit];
  const base = `Système de surpression, ${tier.label.toLowerCase()}`;
  return pompageState.bascule ? `${base} + bascule eau de ville` : base;
};
